import { ZodError } from "zod/v4";
import { HALLIDAY_DOMAIN, WIDGET_HEIGHT, WIDGET_WIDTH } from "@shared/constants";
import { MessageType, PaymentsWidgetQueryParams } from "./types";
import { PaymentStatus } from "../paymentsApiClient/types";

/**
 * Serialize the query params to a base64 string.
 *
 * @param {PaymentsWidgetQueryParams} data {{@link PaymentsWidgetQueryParams}} The query params to serialize.
 * @returns The serialized (base64) query params
 */
export const serializeQueryParams = (data: PaymentsWidgetQueryParams): string => {
    const validatedData = PaymentsWidgetQueryParams.parse(data);
    return btoa(JSON.stringify(validatedData));
};

/**
 * Deserialize the query params from a base64 string.
 *
 * @param {string} serialized The base64 string to deserialize.
 * @returns {{@link PaymentsWidgetQueryParams}} The deserialized query params.
 */
export const deserializeQueryParams = (serialized: string): PaymentsWidgetQueryParams => {
    const validatedData = PaymentsWidgetQueryParams.parse(JSON.parse(atob(serialized)));
    return validatedData;
};

/**
 * Get the URL for the payments widget.
 *
 * @param {PaymentsWidgetQueryParams & { windowOrigin?: string }} params {{@link PaymentsWidgetQueryParams}} The query params to serialize.
 * @returns The URL for the payments widget.
 */
export const getPaymentsWidgetUrl = (
    params: PaymentsWidgetQueryParams & {
        windowOrigin?: string;
    }
): string => {
    const { windowOrigin = HALLIDAY_DOMAIN, ...queryParams } = params;
    const dataString = serializeQueryParams(queryParams);

    const widgetVersion = queryParams.widgetVersion;
    if (widgetVersion) {
        try {
            const version = parseInt(widgetVersion);
            return `${windowOrigin}/v${version}/payments/quote?data=${dataString}`;
        } catch (error) {
            console.error("Error parsing widget version", error);
        }
    }

    return `${windowOrigin}/payments/quote?data=${dataString}`;
};

/**
 * Open the payments widget.
 * Handles both popup and iframe modes. Serializes the query params and opens the widget in the appropriate mode.
 *
 * @param {PaymentsWidgetQueryParams & { windowOrigin?: string }} params {{@link PaymentsWidgetQueryParams}} The query params to serialize.
 * @returns The window object for the payments widget.
 */
export const _openPaymentsWidget = (
    params: PaymentsWidgetQueryParams & {
        windowOrigin?: string;
    }
): Window | null => {
    // On mobile, the width and height are ignored. The popup will be a normal full screen tab.
    const width = WIDGET_WIDTH;
    const height = WIDGET_HEIGHT;
    const windowWidth = window.innerWidth;

    const left = windowWidth / 2 - width / 2;
    const top = 70;

    const { windowType = "POPUP", targetElementId, windowOrigin } = params;

    const widgetUrl = getPaymentsWidgetUrl(params);

    let paymentsWindow: Window | null;

    if (windowType === "EMBED") {
        const targetElement = document.getElementById(targetElementId as string);
        if (!targetElement) {
            throw new Error(`Element with id ${targetElementId} not found`);
        }
        const iframe = document.createElement("iframe");
        iframe.id = "halliday-payments-embed";
        iframe.src = widgetUrl;
        iframe.style.width = width + "px";
        iframe.style.height = height + "px";
        iframe.style.maxWidth = "100%";
        iframe.style.maxHeight = "100%";
        targetElement.innerHTML = "";
        targetElement.appendChild(iframe);

        const closeMessageHandler = (event: MessageEvent) => {
            // Verify message origin
            if (event.origin !== windowOrigin || event.source !== paymentsWindow) {
                return;
            }

            const { type } = event.data || {};

            if (type === MessageType.EVENT_WINDOW_CLOSE) {
                window.removeEventListener("message", closeMessageHandler);
                targetElement.innerHTML = "";
            }
        };
        window.addEventListener("message", closeMessageHandler);

        paymentsWindow = iframe.contentWindow;
    } else if (windowType === "MODAL") {
        let iframe = document.getElementById("halliday-payments-modal") as HTMLIFrameElement;
        const existingLocation = iframe ? iframe.src : null;
        if (existingLocation === widgetUrl) {
            iframe.style.display = "block";
        } else {
            if (iframe) {
                iframe.remove();
            }
            // Create iframe
            iframe = document.createElement("iframe");
            iframe.id = "halliday-payments-modal";
            iframe.src = widgetUrl;
            iframe.style.position = "fixed";
            iframe.style.top = "0";
            iframe.style.left = "0";
            iframe.style.width = "100dvw";
            iframe.style.height = "100dvh";
            iframe.style.border = "none";
            iframe.style.zIndex = "1000";
            iframe.setAttribute("scrolling", "no");
            document.body.appendChild(iframe);
        }

        const closeMessageHandler = (event: MessageEvent) => {
            // Verify message origin
            if (event.origin !== windowOrigin || event.source !== paymentsWindow) {
                return;
            }

            const { type } = event.data || {};

            if (type === MessageType.EVENT_WINDOW_CLOSE) {
                window.removeEventListener("message", closeMessageHandler);
                const modal = document.getElementById("halliday-payments-modal");
                if (modal) {
                    modal.style.display = "none";
                }
            }
        };
        window.addEventListener("message", closeMessageHandler);

        paymentsWindow = iframe.contentWindow;
    } else {
        paymentsWindow = window.open(
            widgetUrl,
            "Halliday Payments",
            `popup left=${left} top=${top} width=${width} height=${height}`
        );
    }

    return paymentsWindow;
};

/**
 * Open the provider widget in a popup window.
 *
 * @param {string} url {{string}} The URL for the provider widget.
 * @returns The window object for the provider widget.
 */
export const _openProviderWidget = (url: string) => {
    const width = WIDGET_WIDTH;
    const height = WIDGET_HEIGHT;
    const windowWidth = window.innerWidth;

    const left = windowWidth / 2 - width / 2;
    const top = 70;

    return window.open(url, "Provider Widget", `popup left=${left} top=${top} width=${width} height=${height}`);
};

/**
 * Fetch the payment status.
 * Retries up to 3 times with exponential backoff when the request fails.
 *
 * XXX TODO: Remove or update this function to hit the new v2 endpoint.
 *
 * @param {string} apiKey {{string}} The API key for the payments API.
 * @param {string} baseURL {{string}} The base URL for the payments API.
 * @param {string} workflowId {{string}} The ID of the workflow to fetch the status for.
 * @returns {Promise<PaymentStatus>} Promise that resolves to the {@link PaymentStatus}.
 */
export const _fetchPaymentStatus = async ({
    apiKey,
    baseURL,
    workflowId,
}: {
    apiKey: string;
    baseURL: string;
    workflowId: string;
}): Promise<PaymentStatus> => {
    const maxRetries = 3;
    let lastError: Error;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
            const response = await fetch(`${baseURL}/quotes/status?workflow_id=${workflowId}`, {
                headers: {
                    Authorization: `Bearer ${apiKey}`,
                },
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            return await response.json();
        } catch (error) {
            lastError = error instanceof Error ? error : new Error(String(error));

            if (attempt === maxRetries) {
                throw lastError;
            }

            // Wait before retrying (exponential backoff)
            const delay = Math.pow(2, attempt) * 1000;
            await new Promise((resolve) => setTimeout(resolve, delay));
        }
    }

    throw lastError!;
};

/**
 * Wait for the payment to be submitted or the window to be closed or fetch fails.
 *
 * @param {string} apiKey {{string}} The API key for the payments API.
 * @param {string} baseURL {{string}} The base URL for the payments API.
 * @param {Window} window {{Window}} The window object for the payments widget.
 * @param {string} workflowId {{string}} The workflow ID of the payment to fetch the status for.
 */
export const _waitForPaymentStatus = async ({
    apiKey,
    baseURL,
    window,
    workflowId,
}: {
    apiKey: string;
    baseURL: string;
    window: Window;
    workflowId: string;
}) => {
    while (!window.closed) {
        let statusResponse: PaymentStatus;
        try {
            statusResponse = await _fetchPaymentStatus({ apiKey, baseURL, workflowId });
        } catch (error) {
            console.error("Error fetching payment status", error);
            break;
        }
        const observedFundingStep = statusResponse.fulfilled.route.find((detail) => detail.type === "ONRAMP");
        const observedFundingStepStatus = observedFundingStep?.status;
        if (observedFundingStepStatus === "COMPLETE") {
            // Wait for payment being completed
            break;
        }
        await new Promise((resolve) => setTimeout(resolve, 5000));
    }
};

export const logError = (error: unknown) => {
    if (error instanceof ZodError) {
        error.issues.forEach((issue) => {
            console.error("Validation Error", {
                path: issue.path.join("."),
                message: issue.message,
            });
        });
    } else {
        console.error("openHallidayPayments Error", { error });
    }
};
